import { capitalizeTerm } from "@loot-os/api-contract";

import { useTenant } from "../../../../lib/tenant.js";
import { useTerminology } from "../../../../lib/terminology.js";
import type { TemplateLink } from "../../shared/links.js";

/** Prose with inline links: a template renders each link with its own styling. */
export type RichText = Array<string | { text: string; link: TemplateLink }>;

export interface FaqEntry {
  question: string;
  answer: RichText;
}

export interface FaqContent {
  title: string;
  entries: FaqEntry[];
}

/**
 * The FAQ. Delivery is worded as arranged through the store: withdrawing is a secret-key route
 * the tenant's own server calls, never something this SPA offers.
 */
export function useFaqContent(): FaqContent {
  const { config } = useTenant();
  const { box, item, inventory } = useTerminology();
  const currency = config.currency.namePlural || config.currency.name;

  return {
    title: "Frequently Asked Questions",
    entries: [
      {
        question: `How does opening ${box.plural} work?`,
        answer: [
          `Every ${box.singular} lists its full odds before you open it. When you open one, the ${item.plural} are revealed straight away and held against your balance; claiming them is what completes the purchase and moves them into your ${inventory.singular}.`,
        ],
      },
      {
        question: "How do I know the odds are real?",
        answer: [
          "Every open is provably fair: the result is fixed by a server seed published in advance and your own client seed, so neither side can change it after the fact. You can check any open yourself on the ",
          { text: "fairness page", link: { to: "/fairness" } },
          ".",
        ],
      },
      {
        question: "What can I do with what I open?",
        answer: [
          `Keep it in your ${inventory.singular}, or sell it back to ${config.name} for its listed value — in full, with no fee. Selling back is all or nothing for the ${item.plural} you pick.`,
        ],
      },
      {
        question: `Can I get the physical ${item.singular}?`,
        answer: [
          `Yes. ${capitalizeTerm(item.plural)} you have claimed can be sent out as real goods, arranged through ${config.name}, with shipping charged for the destination you choose.`,
        ],
      },
      {
        question: `What are ${currency}?`,
        answer: [
          `${currency} are the store's balance. Prices, your balance and anything you sell back are all shown in ${currency}.`,
        ],
      },
    ],
  };
}
